import { LoaderCircle, type LucideIcon } from "lucide-react";
import * as React from "react";

import { Tooltip } from "../Tooltip";

import styles from "./StatusBadge.module.css";

export type StatusTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface StatusBadgeLookProps {
  /** The pill's colour: what kind of state this is, not which status. */
  tone: StatusTone;
  /** Always drawn, before the label, in the label's colour. */
  icon: LucideIcon;
  /** The status's name, in sentence case: "In progress". */
  label: string;
}

export interface StatusBadgeProps extends StatusBadgeLookProps {
  /** Detail shown on hover: a time, an error, what the status means. */
  tooltip?: React.ReactNode;
  /** Makes the badge a button, as for a badge that opens a popup. */
  onClick?: () => void;
  /** The badge's accessible name, where the label alone would not say enough. Defaults to `label`. */
  "aria-label"?: string;
  /** Set on the badge's outer element: the button, or the span around the pill. */
  "data-testid"?: string;
}

/**
 * A status as a small filled pill: a tinted background, a darker label and an
 * icon, in one of five tones. Every status in the app draws through this, so
 * statuses read alike everywhere. A category such as a device type is a
 * `Badge` with the `label` variant instead.
 */
export const StatusBadge = React.forwardRef<HTMLElement, StatusBadgeProps>(
  function StatusBadge(
    {
      tone,
      icon,
      label,
      tooltip,
      onClick,
      "aria-label": ariaLabel,
      "data-testid": testId,
      ...rest
    },
    ref,
  ) {
    const content = <StatusBadgeLook tone={tone} icon={icon} label={label} />;
    const badge = onClick ? (
      <button
        {...rest}
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={styles.button}
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {content}
      </button>
    ) : (
      <span
        {...rest}
        ref={ref}
        className={styles.static}
        aria-label={ariaLabel}
        data-testid={testId}
        tabIndex={tooltip === undefined ? undefined : 0}
      >
        {content}
      </span>
    );
    if (tooltip === undefined) {
      return badge;
    }
    return (
      <Tooltip>
        <Tooltip.Trigger delay={250} render={badge} />
        <Tooltip.Content>{tooltip}</Tooltip.Content>
      </Tooltip>
    );
  },
);

/**
 * Just the pill, for a component that supplies its own trigger, such as a
 * badge that opens an explanation.
 */
export function StatusBadgeLook({
  tone,
  icon: Icon,
  label,
}: StatusBadgeLookProps) {
  return (
    <span className={styles.badge} data-tone={tone} data-status-badge="">
      {/* A loader means work under way, so it turns. */}
      <Icon
        aria-hidden
        className={Icon === LoaderCircle ? styles.spinningIcon : styles.icon}
      />
      {label}
    </span>
  );
}
